import Phaser from 'phaser';
import { SceneKeys } from '../game/Constants';
import { GameState } from '../game/GameState';
import { AssetRegistry } from '../systems/AssetRegistry';
import { DataRegistry } from '../systems/DataRegistry';
import { MissionManager } from '../systems/MissionManager';
import { TechnologyManager } from '../systems/TechnologyManager';
import type { DoctrineData, MissionData, TechnologyData, TechnologyEra } from '../types/data';
import type { ResearchFocus } from '../types/game';
import { VisualTheme } from '../ui/VisualTheme';
import { createTextButton } from '../ui/components/UIButton';
import { addBackButton, drawPanel, drawScreenBackground } from '../ui/components/SceneChrome';

const TABS = ['Technology Tree', 'Institutions', 'Doctrines', 'Missions'] as const;
type TechTab = typeof TABS[number];
const ERAS: Array<{ id: TechnologyEra; label: string; period: string }> = [
  { id: 'foundations', label: 'Foundations', period: 'Court, village and coastal traditions' },
  { id: 'regional', label: 'Regional Exchange', period: 'Networks of ports, schools and states' },
  { id: 'gunpowder', label: 'Gunpowder Age', period: 'Foundries, print and oceanic reach' },
  { id: 'company', label: 'Company Age', period: 'Charters, professional forces and science' },
];
const FOCUS_OPTIONS: ResearchFocus[] = ['balanced', 'administration', 'economy', 'military', 'naval', 'culture'];

export class TechScene extends Phaser.Scene {
  private activeTab: TechTab = 'Technology Tree';
  private eraIndex = 0;
  private selectedTechnologyId?: string;
  private selectedMissionIndex = 0;

  constructor() {
    super(SceneKeys.Tech);
  }

  create() {
    TechnologyManager.instance.ensureCampaignInitialized();
    MissionManager.instance.ensureCampaignInitialized();
    this.render();
  }

  private render() {
    this.children.removeAll();
    TechnologyManager.instance.ensureCampaignInitialized();
    MissionManager.instance.ensureCampaignInitialized();
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    drawScreenBackground(this, 'Knowledge, Institutions and Missions', 'Shape the long arc of the realm through research, doctrine, institutional adoption and authored objectives');
    const backdrop = AssetRegistry.instance.image('background_grand_scriptorium', 'fallback.art');
    this.add.image(800, 500, backdrop).setDisplaySize(1600, 900).setAlpha(0.28).setDepth(-2);
    this.add.rectangle(800, 500, 1550, 735, VisualTheme.colors.ink, 0.73).setDepth(-1);
    addBackButton(this);
    this.drawTabs();
    this.drawSummary();

    if (this.activeTab === 'Technology Tree') this.drawTechnologyTree();
    else if (this.activeTab === 'Institutions') this.drawInstitutions();
    else if (this.activeTab === 'Doctrines') this.drawDoctrines();
    else this.drawMissions();

    const exposed = window as unknown as { render_game_to_text?: () => string; advanceTime?: (ms: number) => void };
    exposed.render_game_to_text = () => JSON.stringify({
      mode: 'technology_missions',
      tab: this.activeTab,
      activeResearch: faction.research.activeTechnologyId,
      researchProgress: TechnologyManager.instance.progress(faction.id),
      monthlyKnowledge: faction.research.monthlyKnowledge,
      scholars: faction.research.scholars,
      adoptedInstitutions: faction.research.adoptedInstitutions,
      landDoctrine: faction.research.landDoctrineId,
      navalDoctrine: faction.research.navalDoctrineId,
      missions: MissionManager.instance.applicableMissions(faction.id).map((mission) => ({ id: mission.id, status: MissionManager.instance.state(faction.id, mission.id)?.status })),
    });
    exposed.advanceTime = () => undefined;
  }

  private drawTabs() {
    TABS.forEach((tab, index) => createTextButton(this, {
      label: tab,
      x: 380 + index * 280,
      y: 112,
      width: 250,
      height: 38,
      fontSize: 14,
      onClick: () => { this.activeTab = tab; this.render(); },
    }));
    const selected = TABS.indexOf(this.activeTab);
    this.add.rectangle(380 + selected * 280, 134, 220, 3, VisualTheme.colors.goldBright, 1);
  }

  private drawSummary() {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const metrics = [
      ['KNOWLEDGE', Math.round(faction.research.knowledge), '#f0d78a'],
      ['MONTHLY', `+${faction.research.monthlyKnowledge || '—'}`, '#9ed6b3'],
      ['SCHOLARS', `${faction.research.scholars}/5`, '#d9c6ef'],
      ['INSTITUTIONS', faction.research.adoptedInstitutions.length, '#8fc8df'],
      ['MISSION SCORE', `${campaign.victory.mission ?? 0}%`, '#f0b878'],
    ] as const;
    metrics.forEach(([label, value, color], index) => {
      const x = 246 + index * 260;
      this.add.rectangle(x, 170, 230, 54, VisualTheme.colors.slate, 0.9).setStrokeStyle(1, VisualTheme.colors.gold, 0.45);
      this.add.text(x - 100, 150, label, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.muted });
      this.add.text(x - 100, 166, String(value), { fontFamily: VisualTheme.fonts.display, fontSize: '20px', color });
    });
  }

  private drawTechnologyTree() {
    const campaign = GameState.instance.requireCampaign();
    const factionId = campaign.playerFactionId;
    const faction = campaign.factions[factionId];
    const era = ERAS[this.eraIndex % ERAS.length];
    drawPanel(this, 620, 535, 1120, 640, `${era.label} — ${era.period}`);
    drawPanel(this, 1330, 535, 360, 640, 'Research Council');

    ERAS.forEach((entry, index) => createTextButton(this, {
      label: entry.label,
      x: 180 + index * 285,
      y: 230,
      width: 250,
      height: 34,
      fontSize: 12,
      onClick: () => { this.eraIndex = index; this.selectedTechnologyId = undefined; this.render(); },
    }));
    this.add.rectangle(180 + this.eraIndex * 285, 250, 210, 3, VisualTheme.colors.goldBright, 1);

    const technologies = DataRegistry.instance.technologyList().filter((technology) => technology.era === era.id).sort((a, b) => a.tier - b.tier || a.category.localeCompare(b.category));
    this.selectedTechnologyId ??= technologies[0]?.id;
    technologies.slice(0, 6).forEach((technology, index) => this.drawTechnologyCard(technology, 105 + (index % 3) * 355, 295 + Math.floor(index / 3) * 235));
    const selected = this.selectedTechnologyId ? DataRegistry.instance.technologies[this.selectedTechnologyId] : undefined;
    if (selected) this.drawTechnologyDetail(selected);

    this.add.text(1170, 705, 'RESEARCH FOCUS', { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.muted });
    FOCUS_OPTIONS.forEach((focus, index) => createTextButton(this, {
      label: focus.replaceAll('_', ' '),
      x: 1242 + (index % 2) * 168,
      y: 744 + Math.floor(index / 2) * 42,
      width: 150,
      height: 32,
      fontSize: 10,
      onClick: () => { TechnologyManager.instance.setFocus(factionId, focus); this.render(); },
    }));
    this.add.text(1170, 842, `Current focus: ${faction.research.focus}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.accent });
  }

  private drawTechnologyCard(technology: TechnologyData, x: number, y: number) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const researched = faction.researchedTechs.includes(technology.id);
    const active = faction.research.activeTechnologyId === technology.id;
    const available = TechnologyManager.instance.canResearch(faction.id, technology.id);
    const selected = this.selectedTechnologyId === technology.id;
    const accent = researched ? 0x4b9b70 : active ? VisualTheme.colors.goldBright : available ? 0x4f7892 : 0x5d5660;
    this.add.rectangle(x + 155, y + 88, 325, 196, VisualTheme.colors.slate, 0.94).setStrokeStyle(selected ? 3 : 2, accent, selected ? 1 : 0.7);
    this.add.rectangle(x + 10, y + 14, 6, 145, accent, 1);
    this.add.text(x + 24, y + 6, technology.name, { fontFamily: VisualTheme.fonts.display, fontSize: '19px', color: VisualTheme.text.title, wordWrap: { width: 275 } });
    this.add.text(x + 24, y + 38, `${technology.category.toUpperCase()}  •  TIER ${technology.tier}`, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.accent });
    this.add.text(x + 24, y + 61, technology.description, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body, wordWrap: { width: 275 }, lineSpacing: 3 });
    const status = researched ? 'RESEARCHED' : active ? `${TechnologyManager.instance.progress(faction.id, technology.id)}% ACTIVE` : available ? `COST ${TechnologyManager.instance.cost(faction.id, technology.id)} KNOWLEDGE` : 'LOCKED';
    this.add.text(x + 24, y + 149, status, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: researched ? '#9ed6b3' : active ? '#f0d78a' : available ? '#8fc8df' : '#ba9292' });
    if (active) this.drawProgressBar(x + 24, y + 176, 276, TechnologyManager.instance.progress(faction.id, technology.id), VisualTheme.colors.goldBright);
    const hit = this.add.rectangle(x + 155, y + 88, 325, 196, 0xffffff, 0).setInteractive({ useHandCursor: true });
    hit.on('pointerdown', () => { this.selectedTechnologyId = technology.id; this.render(); });
  }

  private drawTechnologyDetail(technology: TechnologyData) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const researched = faction.researchedTechs.includes(technology.id);
    const active = faction.research.activeTechnologyId === technology.id;
    const available = TechnologyManager.instance.canResearch(faction.id, technology.id);
    this.add.text(1170, 270, technology.name, { fontFamily: VisualTheme.fonts.display, fontSize: '25px', color: VisualTheme.text.title, wordWrap: { width: 300 } });
    this.add.text(1170, 330, `${technology.era.toUpperCase()}  •  ${technology.category.toUpperCase()}  •  TIER ${technology.tier}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.accent });
    this.add.text(1170, 360, technology.description, { fontFamily: VisualTheme.fonts.body, fontSize: '13px', color: VisualTheme.text.body, wordWrap: { width: 300 }, lineSpacing: 5 });
    const prerequisites = (technology.prerequisites ?? []).map((id) => DataRegistry.instance.technologies[id]?.name ?? id).join(', ') || 'None';
    const institution = technology.requiredInstitution ? DataRegistry.instance.institutions[technology.requiredInstitution]?.name ?? technology.requiredInstitution : 'None';
    this.add.text(1170, 455, `Prerequisites
${prerequisites}

Institution
${institution}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.muted, wordWrap: { width: 300 }, lineSpacing: 4 });
    const effects = Object.entries(technology.effects ?? {}).map(([key, value]) => `${key.replaceAll(/([A-Z])/g, ' $1')}: ${value}`).join('\n') || 'Narrative and unlock effects';
    this.add.text(1170, 565, `EFFECTS\n${effects}`, { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: '#b9d9c8', wordWrap: { width: 300 }, lineSpacing: 3 });
    createTextButton(this, {
      label: researched ? 'Knowledge Secured' : active ? 'Research in Progress' : available ? 'Begin Research' : 'Requirements Missing',
      x: 1330,
      y: 665,
      width: 300,
      height: 42,
      fontSize: 13,
      onClick: () => { if (available && !researched) TechnologyManager.instance.selectResearch(faction.id, technology.id); this.render(); },
    });
    createTextButton(this, {
      label: `Sponsor Scholar (${55 + faction.research.scholars * 25} gold)`,
      x: 1330,
      y: 790,
      width: 300,
      height: 38,
      fontSize: 12,
      onClick: () => { TechnologyManager.instance.sponsorScholar(faction.id); this.render(); },
    });
  }

  private drawInstitutions() {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    drawPanel(this, 800, 535, 1460, 640, 'Institutions — regional practices spread through ports, scholars, allies and rivals');
    DataRegistry.instance.institutionList().forEach((institution, index) => {
      const x = 95 + (index % 3) * 500;
      const y = 255 + Math.floor(index / 3) * 280;
      const state = campaign.institutions[institution.id];
      const progress = Math.round(faction.research.institutionProgress[institution.id] ?? 0);
      const adopted = faction.research.adoptedInstitutions.includes(institution.id);
      const discovered = state && campaign.turn >= state.discoveredTurn;
      const accent = adopted ? 0x4b9b70 : discovered ? VisualTheme.colors.gold : 0x5d5660;
      this.add.rectangle(x + 215, y + 108, 440, 230, VisualTheme.colors.slate, 0.94).setStrokeStyle(2, accent, 0.85);
      this.add.text(x + 22, y + 14, institution.name, { fontFamily: VisualTheme.fonts.display, fontSize: '21px', color: VisualTheme.text.title });
      this.add.text(x + 22, y + 47, `${institution.era.toUpperCase()}  •  ORIGIN ${institution.originYear}`, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: VisualTheme.text.accent });
      this.add.text(x + 22, y + 72, institution.description, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body, wordWrap: { width: 385 }, lineSpacing: 4 });
      this.drawProgressBar(x + 22, y + 154, 385, adopted ? 100 : Math.min(100, progress), adopted ? 0x4b9b70 : VisualTheme.colors.goldBright);
      this.add.text(x + 22, y + 172, adopted ? 'ADOPTED' : discovered ? `SPREAD ${Math.min(100, progress)}%  •  GLOBAL ${Math.round(state?.globalSpread ?? 0)}%` : `EXPECTED AROUND ${institution.originYear}`, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: adopted ? '#9ed6b3' : VisualTheme.text.muted });
      createTextButton(this, {
        label: adopted ? 'Institution Adopted' : progress >= 100 ? `Adopt (${TechnologyManager.instance.institutionCost(faction.id, institution.id)} gold)` : 'Spreading',
        x: x + 215,
        y: y + 207,
        width: 270,
        height: 34,
        fontSize: 11,
        onClick: () => { if (!adopted && progress >= 100) TechnologyManager.instance.adoptInstitution(faction.id, institution.id); this.render(); },
      });
    });
  }

  private drawDoctrines() {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    drawPanel(this, 800, 535, 1460, 640, 'Military and Naval Doctrines');
    this.add.text(88, 238, 'LAND DOCTRINES', { fontFamily: VisualTheme.fonts.display, fontSize: '19px', color: VisualTheme.text.title });
    this.add.text(88, 525, 'NAVAL DOCTRINES', { fontFamily: VisualTheme.fonts.display, fontSize: '19px', color: VisualTheme.text.title });
    const land = DataRegistry.instance.doctrineList().filter((doctrine) => doctrine.branch === 'land');
    const naval = DataRegistry.instance.doctrineList().filter((doctrine) => doctrine.branch === 'naval');
    land.forEach((doctrine, index) => this.drawDoctrineCard(doctrine, 90 + index * 370, 275, faction.research.landDoctrineId === doctrine.id));
    naval.forEach((doctrine, index) => this.drawDoctrineCard(doctrine, 90 + index * 370, 560, faction.research.navalDoctrineId === doctrine.id));
  }

  private drawDoctrineCard(doctrine: DoctrineData, x: number, y: number, adopted: boolean) {
    const campaign = GameState.instance.requireCampaign();
    const faction = campaign.factions[campaign.playerFactionId];
    const available = TechnologyManager.instance.doctrineAvailable(faction.id, doctrine.id);
    const accent = adopted ? VisualTheme.colors.goldBright : available ? 0x4f7892 : 0x5d5660;
    this.add.rectangle(x + 160, y + 92, 340, 205, VisualTheme.colors.slate, 0.94).setStrokeStyle(adopted ? 3 : 2, accent, 0.85);
    this.add.text(x + 18, y + 10, doctrine.name, { fontFamily: VisualTheme.fonts.display, fontSize: '19px', color: VisualTheme.text.title, wordWrap: { width: 285 } });
    this.add.text(x + 18, y + 43, doctrine.description, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: VisualTheme.text.body, wordWrap: { width: 285 }, lineSpacing: 4 });
    const requirements = (doctrine.requiredTechs ?? []).map((id) => DataRegistry.instance.technologies[id]?.name ?? id).join(', ') || 'None';
    this.add.text(x + 18, y + 117, `Requires: ${requirements}`, { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: available ? VisualTheme.text.muted : '#ba9292', wordWrap: { width: 285 } });
    const tradition = Math.round(faction.research.doctrineTradition[doctrine.id] ?? 0);
    this.add.text(x + 18, y + 145, adopted ? `ACTIVE  •  TRADITION ${tradition}` : available ? 'AVAILABLE' : 'LOCKED', { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: adopted ? '#f0d78a' : available ? '#8fc8df' : '#ba9292' });
    createTextButton(this, {
      label: adopted ? 'Active Doctrine' : available ? 'Adopt Doctrine' : 'Technology Required',
      x: x + 160,
      y: y + 184,
      width: 255,
      height: 32,
      fontSize: 10,
      onClick: () => { if (available && !adopted) TechnologyManager.instance.adoptDoctrine(faction.id, doctrine.id); this.render(); },
    });
  }

  private drawMissions() {
    const campaign = GameState.instance.requireCampaign();
    const factionId = campaign.playerFactionId;
    const missions = MissionManager.instance.applicableMissions(factionId);
    this.selectedMissionIndex = Phaser.Math.Clamp(this.selectedMissionIndex, 0, Math.max(0, missions.length - 1));
    const selected = missions[this.selectedMissionIndex];
    drawPanel(this, 490, 535, 820, 640, 'Mission Tree');
    drawPanel(this, 1210, 535, 590, 640, selected?.title ?? 'Mission Detail');
    missions.slice(0, 10).forEach((mission, index) => {
      const state = MissionManager.instance.state(factionId, mission.id);
      const y = 250 + index * 57;
      const selectedRow = index === this.selectedMissionIndex;
      const color = state?.status === 'claimed' ? 0x4b9b70 : state?.status === 'completed' ? VisualTheme.colors.goldBright : state?.status === 'active' ? 0x4f7892 : state?.status === 'locked' ? 0x5d5660 : VisualTheme.colors.gold;
      this.add.rectangle(480, y, 730, 48, selectedRow ? VisualTheme.colors.panelHover : VisualTheme.colors.slate, 0.92).setStrokeStyle(selectedRow ? 2 : 1, color, 0.8);
      this.add.text(135, y - 16, mission.title, { fontFamily: VisualTheme.fonts.display, fontSize: '15px', color: VisualTheme.text.title });
      this.add.text(650, y - 14, (state?.status ?? 'locked').toUpperCase(), { fontFamily: VisualTheme.fonts.body, fontSize: '10px', color: this.hex(color) });
      const hit = this.add.rectangle(480, y, 730, 48, 0xffffff, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => { this.selectedMissionIndex = index; campaign.selectedMissionId = mission.id; this.render(); });
    });
    if (selected) this.drawMissionDetail(selected);
  }

  private drawMissionDetail(mission: MissionData) {
    const campaign = GameState.instance.requireCampaign();
    const factionId = campaign.playerFactionId;
    const state = MissionManager.instance.state(factionId, mission.id);
    if (!state) return;
    this.add.text(945, 255, mission.category.toUpperCase(), { fontFamily: VisualTheme.fonts.body, fontSize: '11px', color: VisualTheme.text.accent });
    this.add.text(945, 286, mission.description, { fontFamily: VisualTheme.fonts.body, fontSize: '14px', color: VisualTheme.text.body, wordWrap: { width: 500 }, lineSpacing: 5 });
    this.add.text(945, 374, 'OBJECTIVES', { fontFamily: VisualTheme.fonts.display, fontSize: '17px', color: VisualTheme.text.title });
    mission.objectives.forEach((objective, index) => {
      const progress = state.progress[index] ?? MissionManager.instance.objectiveProgress(factionId, objective);
      const complete = progress >= objective.value;
      const y = 410 + index * 78;
      this.add.rectangle(1205, y + 20, 510, 62, VisualTheme.colors.slate, 0.86).setStrokeStyle(1, complete ? 0x4b9b70 : 0x596f82, 0.8);
      this.add.text(968, y, objective.description, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: complete ? '#9ed6b3' : VisualTheme.text.body, wordWrap: { width: 360 } });
      this.add.text(1395, y, `${Math.min(objective.value, Math.round(progress))}/${objective.value}`, { fontFamily: VisualTheme.fonts.display, fontSize: '15px', color: complete ? '#9ed6b3' : '#f0d78a' });
      this.drawProgressBar(968, y + 36, 430, Math.min(100, progress / Math.max(1, objective.value) * 100), complete ? 0x4b9b70 : VisualTheme.colors.goldBright);
    });
    const rewardsY = 430 + mission.objectives.length * 78;
    this.add.text(945, rewardsY, `REWARD\n${MissionManager.instance.rewardSummary(mission)}`, { fontFamily: VisualTheme.fonts.body, fontSize: '12px', color: '#b9d9c8', wordWrap: { width: 500 }, lineSpacing: 4 });
    createTextButton(this, {
      label: state.status === 'claimed' ? 'Reward Claimed' : state.status === 'completed' ? 'Claim Reward' : state.status === 'active' ? 'Mission Active' : state.status === 'available' ? 'Begin Mission' : 'Prerequisites Locked',
      x: 1210,
      y: 800,
      width: 420,
      height: 44,
      fontSize: 13,
      onClick: () => {
        if (state.status === 'available') MissionManager.instance.startMission(factionId, mission.id);
        else if (state.status === 'completed') MissionManager.instance.claimReward(factionId, mission.id);
        this.render();
      },
    });
  }

  private drawProgressBar(x: number, y: number, width: number, percent: number, color: number) {
    this.add.rectangle(x, y, width, 10, VisualTheme.colors.ink, 0.9).setOrigin(0).setStrokeStyle(1, VisualTheme.colors.gold, 0.45);
    this.add.rectangle(x + 2, y + 2, Math.max(0, width - 4) * Phaser.Math.Clamp(percent / 100, 0, 1), 6, color, 0.95).setOrigin(0);
  }

  private hex(color: number) { return `#${color.toString(16).padStart(6, '0')}`; }
}